<template>
  <Datepicker
    auto-apply
    :enable-time-picker="false"
    :clearable="false"
    :offset="8"
    :month-change-on-scroll="false"
    v-bind="$attrs"
    :disabled="disabled"
    :readonly="readonly"
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #dp-input="{ value, isMenuOpen }">
      <button
        type="button"
        class="field-shell group h-10 w-full gap-1.5 px-3 text-body font-medium
               text-text [font-family:inherit] focus-visible:outline-none"
        :class="[
          disabled ? 'is-disabled' : readonly ? 'is-readonly'
            : 'cursor-pointer hover:border-accent focus-visible:border-accent',
          isMenuOpen ? 'border-accent' : '',
        ]"
      >
        <IconCalendarDateRange20Solid class="size-4 shrink-0 text-text-muted" />
        <span class="flex-1 truncate text-left" :class="value ? '' : 'font-normal text-text-muted'">
          {{ value || (readonly ? '—' : placeholder) }}
        </span>
        <span
          v-if="showClear && !readonly"
          class="flex size-4 shrink-0 cursor-pointer items-center justify-center
                 text-text-muted transition-colors duration-150 hover:text-accent"
          @click.stop="$emit('clear')"
        >
          <IconXMark16Solid class="size-4" />
        </span>
        <IconLockClosed16Solid v-if="readonly" class="size-4 shrink-0 text-text-muted" />
        <IconChevronDown
          v-else
          class="pointer-events-none size-4 shrink-0 transition-[rotate,color] duration-300
                 ease-out group-hover:text-accent"
          :class="isMenuOpen ? 'rotate-180 text-accent' : 'text-text-muted'"
        />
      </button>
    </template>

    <template v-for="name in Object.keys($slots)" #[name]="slotProps" :key="name">
      <slot :name="name" v-bind="slotProps" />
    </template>
  </Datepicker>
</template>

<script setup>
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import {
  IconXMark16Solid,
  IconChevronDown,
  IconCalendarDateRange20Solid,
  IconLockClosed16Solid,
} from '@iconify-prerendered/vue-heroicons';

defineOptions({ inheritAttrs: false });

defineProps({
  modelValue: { type: [String, Number, Array, Object, Date], default: null },
  placeholder: { type: String, default: '' },
  showClear: Boolean,
  disabled: Boolean,
  // shows the picked dates as they are, the calendar never opens
  readonly: Boolean,
});

defineEmits(['update:modelValue', 'clear']);
</script>

<style lang="scss">

.dp__main[class],
.dp__theme_light[class],
.dp__theme_dark[class] {
    --dp-font-family: inherit;
    --dp-font-size: var(--fs-body);
    --dp-border-radius: var(--chip-radius);
    --dp-cell-border-radius: 8px;
    --dp-cell-size: 32px;
    --dp-cell-padding: 1px 6px;
    --dp-menu-padding: 0;

    --dp-background-color: var(--popover-solid);
    --dp-text-color: var(--text);
    --dp-secondary-color: var(--text-muted);
    --dp-border-color: var(--line);
    --dp-menu-border-color: var(--line);
    --dp-icon-color: var(--text-soft);

    --dp-primary-color: var(--accent);
    --dp-primary-text-color: var(--on-accent);
    --dp-primary-disabled-color: var(--disabled-bg);

    --dp-hover-color: var(--hover);
    --dp-hover-text-color: var(--text);
    --dp-hover-icon-color: var(--accent);

    --dp-range-between-dates-background-color: var(--accent-soft);
    --dp-range-between-dates-text-color: var(--accent);
    --dp-range-between-border-color: transparent;
    --dp-highlight-color: var(--accent-soft);
}

.dp__input_wrap {
    display: flex;
}

.dp--header-wrap[class] {
    margin-bottom: 8px;
}

.dp__calendar_header_separator[class] {
    display: none;
}

/* library ships these as circles; match the month/year selects and day cells */
.dp__inner_nav[class] {
    border-radius: var(--chip-radius);
}

.dp__calendar_header_item[class] {
    height: 16px;
    color: var(--text-muted);
    text-transform: uppercase;
    font-weight: 500;
    font-size: var(--fs-unit);
}

.dp__cell_inner[class] {
    width: var(--dp-cell-size);
    margin-inline: auto;
    font-size: 12px;
    font-weight: 700;
    transition: background 0.12s, color 0.12s;
    border-radius: var(--dp-cell-border-radius);
}

.dp__instance_calendar[class] {
    margin-top: -2px;
    z-index: 10;
}

.dp__arrow_top[class],
.dp__arrow_bottom[class] {
    display: none;
}

.dp--menu-wrapper[class] {
    width: 100%;
}

.dp__menu[class] {
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border-radius: var(--chip-radius);
    box-shadow: var(--shadow-popover);
}

.dp__menu.dp--menu-wrapper[class] {
    width: 18rem;
    pointer-events: auto;
}
</style>
